// Rapport journalier imprimable (A4) — à imprimer ou enregistrer en PDF.
import React, { useEffect, useState } from 'react';
import { useFer } from '../utils/fer';
import { useParams } from 'react-router-dom';
import { settingsAPI, stockAPI } from '../services/api';
import { apiError } from '../utils/format';
import { normalizeReport, reportDate, reportDateLong, reportNumber } from '../utils/report';

const css = `
  @page { size: A4; margin: 12mm; }
  * { box-sizing: border-box; }
  body { margin: 0; background: #e9ecef; font-family: Arial, Helvetica, sans-serif; color: #111; }
  .toolbar { position: sticky; top: 0; z-index: 2; background: #12284C; color: #fff; padding: 10px 16px; display: flex; gap: 12px; align-items: center; }
  .toolbar button { background: #fff; color: #12284C; border: 0; padding: 8px 14px; border-radius: 4px; font-weight: bold; cursor: pointer; }
  .page { width: 186mm; min-height: 273mm; margin: 12px auto; background: #fff; padding: 8mm; display: flex; flex-direction: column; }
  .head { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 3px solid #12284C; padding-bottom: 10px; }
  .head img { height: 58px; }
  .co { font-size: 11px; color: #333; margin-top: 4px; line-height: 1.5; }
  .titlebox { text-align: right; }
  .titlebox .t { font-size: 20px; font-weight: bold; color: #12284C; }
  .titlebox .n { font-size: 12px; margin-top: 4px; }
  .meta { display: flex; justify-content: space-between; margin: 12px 0 14px; font-size: 13px; }
  .meta b { text-transform: capitalize; }
  .kpis { display: flex; gap: 10px; margin-bottom: 16px; }
  .kpi { flex: 1; border: 1px solid #c9ced6; border-radius: 6px; padding: 8px 12px; }
  .kpi .l { font-size: 11px; color: #555; }
  .kpi .v { font-size: 22px; font-weight: bold; color: #12284C; }
  .cols { display: flex; gap: 16px; }
  .col { flex: 1; min-width: 0; }
  h3 { font-size: 13px; margin: 0 0 6px; color: #12284C; text-transform: uppercase; letter-spacing: .04em; }
  table { width: 100%; border-collapse: collapse; font-size: 12.5px; margin-bottom: 14px; }
  th { background: #EEF1F5; text-align: left; padding: 5px 8px; border: 1px solid #c9ced6; font-size: 11.5px; }
  td { padding: 5px 8px; border: 1px solid #c9ced6; }
  td.q, th.q { text-align: right; white-space: nowrap; width: 34%; }
  td .num { font-size: 10.5px; color: #444; margin-top: 2px; word-break: break-word; }
  tr.tot td { font-weight: bold; background: #F7F8FA; }
  .obs { border-left: 4px solid #E8A317; background: #FBF7EE; padding: 8px 12px; font-size: 12.5px; white-space: pre-line; margin-bottom: 14px; }
  .sign { display: flex; gap: 16px; margin-top: auto; padding-top: 24px; }
  .sign div { flex: 1; border: 1px solid #c9ced6; border-radius: 6px; height: 80px; padding: 6px 10px; font-size: 11.5px; color: #444; }
  .foot { font-size: 10px; color: #777; margin-top: 10px; text-align: center; }
  @media print { body { background: #fff; } .toolbar { display: none; } .page { margin: 0; width: auto; min-height: 270mm; padding: 0; } }
`;

const Tbl = ({ head, rows, total }) => (
  <table>
    <thead><tr><th>{head}</th><th className="q">Quantité</th></tr></thead>
    <tbody>
      {rows.map(([k, v, sub]) => <tr key={k}><td>{k}{sub && <div className="num">{sub}</div>}</td><td className="q">{v}</td></tr>)}
      {total !== undefined && <tr className="tot"><td>Total</td><td className="q">{total}</td></tr>}
    </tbody>
  </table>
);

const RapportPrint = () => {
  useFer(); // poids des barres pour l'équivalent en kg
  const { id } = useParams();
  const [r, setR] = useState(null);
  const [s, setS] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    Promise.all([stockAPI.getReport(id), settingsAPI.get()])
      .then(([rep, st]) => {
        const data = rep.data.report || rep.data;
        setR(data); setS(st.data);
        document.title = `Rapport production ${reportDate(data.report_date).replace(/\//g, '-')}`;
      })
      .catch((e) => setError(apiError(e)));
  }, [id]);
  if (error) return <div style={{ padding: 24 }}>{error}</div>;
  if (!r || !s) return <div style={{ padding: 24 }}>Chargement…</div>;
  const n = normalizeReport(r);
  return (
    <>
      <style>{css}</style>
      <div className="toolbar"><strong>Rapport du {reportDate(n.date)}</strong><span style={{ marginRight: 'auto' }}>{n.auteur}</span>
        <button onClick={() => window.print()}>Imprimer / Enregistrer en PDF</button></div>
      <div className="page">
        <div className="head">
          <div>
            <img src="/logo-docc.png" alt="DOCC" />
            <div className="co"><b>{s.raison_sociale}</b><br />{s.adresse}{s.telephone ? ` · Tél : ${s.telephone}` : ''}</div>
          </div>
          <div className="titlebox">
            <div className="t">Rapport journalier<br />de production</div>
            <div className="n">N° {reportNumber(n)}</div>
          </div>
        </div>
        <div className="meta">
          <span>Date : <b>{reportDateLong(n.date)}</b></span>
          <span>Rédigé par : <b>{n.auteur}</b></span>
        </div>
        <div className="kpis">
          <div className="kpi"><div className="l">PBA produits</div><div className="v">{n.totalPba}</div></div>
          <div className="kpi"><div className="l">Armatures façonnées</div><div className="v">{n.totalArmatures}</div></div>
          <div className="kpi"><div className="l">Personnel mobilisé</div><div className="v">{n.totalPersonnel}</div></div>
        </div>
        <div className="cols">
          <div className="col">
            {n.production.length > 0 && (<><h3>Production de PBA</h3><Tbl head="Type" rows={n.production.map((p) => [p.ref ? `${p.code} (réf. ${p.ref})` : p.code, p.quantite, p.numeros && `N° ${p.numeros}`])} total={n.totalPba} /></>)}
            {n.armatures.length > 0 && (<><h3>Armatures façonnées</h3><Tbl head="Type" rows={n.armatures.map((a) => [a.code, a.quantite])} total={n.totalArmatures} /></>)}
          </div>
          <div className="col">
            {n.materiaux.length > 0 && (<><h3>Matériaux utilisés</h3><Tbl head="Matériau" rows={n.materiaux.map((m) => [m.nom, m.quantite])} /></>)}
            {n.personnel.length > 0 && (<><h3>Personnel mobilisé</h3><Tbl head="Fonction" rows={n.personnel.map((p) => [p.fonction, p.quantite])} total={n.totalPersonnel} /></>)}
          </div>
        </div>
        {n.observations && (<><h3>Observations</h3><div className="obs">{n.observations}</div></>)}
        <div className="sign"><div>Chef de production</div><div>Visa du responsable</div></div>
        <div className="foot">{s.raison_sociale} — document généré par l'application DOCC</div>
      </div>
    </>
  );
};

export default RapportPrint;
